import type { Island } from '../types'
import { freshSrs } from '../lib/srs'
import { MORE_ZH } from './islandsMoreZh'
import { EXTRA_ZH } from './islandsExtraZh'
import { NUMBERS_ZH } from './islandsNumbersZh'
import { BEGINNER_ZH } from './islandsBeginnerZh'
import { ADVANCED_ZH } from './islandsAdvancedZh'

/**
 * Starter islands, roughly HSK 3-4.
 *
 * These are deliberately concrete rather than generic — a block you can't
 * picture yourself saying is a block you won't drill. They are also all wrong
 * about you in the details, which is the point: the editor exists so you
 * rewrite them into your own life before they mean anything.
 *
 * Pinyin is stored as it is *spoken*, with 一/不 sandhi applied, because these
 * are performance scripts. The grader exempts those two syllables from tone
 * checking so the dictionary tones are not marked wrong.
 */

export type SeedBlock = [hanzi: string, pinyin: string, english: string, note?: string]

export interface SeedIsland {
  id: string
  title: string
  titleZh: string
  useWhen: string
  tags: string[]
  hskLevel: number
  blocks: SeedBlock[]
}

const SEEDS: SeedIsland[] = [
  {
    id: 'isl_work',
    title: 'My work',
    titleZh: '我的工作',
    useWhen: 'The second question anyone asks. Have all five blocks ready cold.',
    tags: ['introductions', 'work'],
    hskLevel: 3,
    blocks: [
      ['我是软件工程师。', 'wǒ shì ruǎn jiàn gōng chéng shī', "I'm a software engineer."],
      [
        '我在一家科技公司工作，已经五年了。',
        'wǒ zài yì jiā kē jì gōng sī gōng zuò yǐ jīng wǔ nián le',
        "I work at a tech company — five years now.",
        '已经…了 frames a stretch of time still going. Drop the 了 and it sounds finished.',
      ],
      [
        '我主要负责后端开发和系统设计。',
        'wǒ zhǔ yào fù zé hòu duān kāi fā hé xì tǒng shè jì',
        "I'm mainly responsible for back-end development and system design.",
      ],
      [
        '我平时在家上班，只有开会的时候才去公司。',
        'wǒ píng shí zài jiā shàng bān zhǐ yǒu kāi huì de shí hou cái qù gōng sī',
        'I usually work from home; I only go in for meetings.',
        '只有…才 is the "only under this condition" frame. Worth stealing for other topics.',
      ],
      [
        '这份工作压力不小，但是我很喜欢。',
        'zhè fèn gōng zuò yā lì bù xiǎo dàn shì wǒ hěn xǐ huan',
        "The job is pretty stressful, but I like it a lot.",
        '压力不小 — understatement by negation. Sounds far more native than 压力很大.',
      ],
    ],
  },
  {
    id: 'isl_why_chinese',
    title: 'Why I study Chinese',
    titleZh: '我为什么学中文',
    useWhen: 'Fires the moment anyone compliments your Chinese. It always comes.',
    tags: ['introductions', 'language'],
    hskLevel: 3,
    blocks: [
      ['我学中文已经两年多了。', 'wǒ xué zhōng wén yǐ jīng liǎng nián duō le', "I've been studying Chinese for over two years."],
      [
        '一开始只是因为工作需要，后来就真的喜欢上了。',
        'yì kāi shǐ zhǐ shì yīn wèi gōng zuò xū yào hòu lái jiù zhēn de xǐ huan shàng le',
        'At first it was just for work, but then I genuinely fell for it.',
        '喜欢上了 = came to like it. The 上 marks the change of state.',
      ],
      [
        '我觉得最难的是声调，最有意思的是汉字。',
        'wǒ jué de zuì nán de shì shēng diào zuì yǒu yì si de shì hàn zì',
        'I find the tones hardest and the characters most interesting.',
      ],
      ['我每天早上花半个小时练习。', 'wǒ měi tiān zǎo shang huā bàn ge xiǎo shí liàn xí', 'I spend half an hour practising every morning.'],
      [
        '我的目标是能跟中国朋友自然地聊天。',
        'wǒ de mù biāo shì néng gēn zhōng guó péng you zì rán de liáo tiān',
        'My goal is to chat naturally with Chinese friends.',
      ],
    ],
  },
  {
    id: 'isl_family',
    title: 'My family',
    titleZh: '我的家庭',
    useWhen: 'Rewrite this one first. Wrong details here are worse than no island.',
    tags: ['introductions', 'family'],
    hskLevel: 3,
    blocks: [
      ['我家有四口人。', 'wǒ jiā yǒu sì kǒu rén', 'There are four of us.', '口 is the measure word for family members. 个 marks you as a beginner here.'],
      ['我、我太太，还有两个孩子。', 'wǒ wǒ tài tai hái yǒu liǎng ge hái zi', 'Me, my wife, and two kids.'],
      [
        '我太太是老师，在一所中学教英语。',
        'wǒ tài tai shì lǎo shī zài yì suǒ zhōng xué jiāo yīng yǔ',
        'My wife is a teacher; she teaches English at a middle school.',
      ],
      [
        '大的那个上小学，小的还在上幼儿园。',
        'dà de nà ge shàng xiǎo xué xiǎo de hái zài shàng yòu ér yuán',
        "The older one's in primary school, the younger one's still in kindergarten.",
      ],
      [
        '我父母住得不远，周末常常过来吃饭。',
        'wǒ fù mǔ zhù de bù yuǎn zhōu mò cháng cháng guò lai chī fàn',
        'My parents live nearby and often come over for dinner at the weekend.',
      ],
    ],
  },
  {
    id: 'isl_home',
    title: 'Where I live',
    titleZh: '我住的地方',
    useWhen: 'Pairs with the work island. Somebody always asks where you came from.',
    tags: ['introductions', 'place'],
    hskLevel: 3,
    blocks: [
      ['我住在美国中西部的一个小城市。', 'wǒ zhù zài měi guó zhōng xī bù de yí ge xiǎo chéng shì', 'I live in a small city in the American Midwest.'],
      ['这里人不多，生活节奏比较慢。', 'zhè lǐ rén bù duō shēng huó jié zòu bǐ jiào màn', "There aren't many people; the pace of life is fairly slow."],
      ['冬天特别冷，有时候会下大雪。', 'dōng tiān tè bié lěng yǒu shí hou huì xià dà xuě', 'Winter is bitterly cold — heavy snow sometimes.'],
      [
        '从我家开车到市中心大概十五分钟。',
        'cóng wǒ jiā kāi chē dào shì zhōng xīn dà gài shí wǔ fēn zhōng',
        "It's about fifteen minutes by car from my place to downtown.",
        '从…到… is the workhorse frame for any two points, in space or time.',
      ],
    ],
  },
  {
    id: 'isl_hobbies',
    title: 'What I do outside work',
    titleZh: '我的爱好',
    useWhen: 'The island that keeps a conversation going once the basics are done.',
    tags: ['introductions', 'hobbies'],
    hskLevel: 3,
    blocks: [
      ['我平时喜欢看书和跑步。', 'wǒ píng shí xǐ huan kàn shū hé pǎo bù', 'I like reading and running in my spare time.'],
      ['周末我一般会跑十公里左右。', 'zhōu mò wǒ yì bān huì pǎo shí gōng lǐ zuǒ yòu', 'At the weekend I usually run about ten kilometres.'],
      ['除了这些，我还对做饭很感兴趣。', 'chú le zhè xiē wǒ hái duì zuò fàn hěn gǎn xìng qù', "Besides that, I'm really into cooking."],
      [
        '最近我在学做中国菜，不过做得还不太好。',
        'zuì jìn wǒ zài xué zuò zhōng guó cài bú guò zuò de hái bú tài hǎo',
        "Lately I've been learning to cook Chinese food, though I'm still not much good at it.",
        'Ending on modesty invites them to ask what you cook. Useful bait.',
      ],
    ],
  },
  {
    id: 'isl_restaurant',
    title: 'Ordering a meal',
    titleZh: '在餐厅点菜',
    useWhen: 'Transactional, high-frequency, unforgiving of hesitation. Drill to reflex.',
    tags: ['transactional', 'food'],
    hskLevel: 2,
    blocks: [
      ['你好，我们两个人。', 'nǐ hǎo wǒ men liǎng ge rén', 'Hi — table for two.'],
      ['请问，有没有英文菜单？', 'qǐng wèn yǒu méi yǒu yīng wén cài dān', 'Excuse me, do you have an English menu?'],
      [
        '我要一个宫保鸡丁，一个西红柿炒鸡蛋。',
        'wǒ yào yí ge gōng bǎo jī dīng yí ge xī hóng shì chǎo jī dàn',
        "I'll have the kung pao chicken and the tomato and egg.",
      ],
      [
        '请不要放太多辣椒，我不太能吃辣。',
        'qǐng bú yào fàng tài duō là jiāo wǒ bú tài néng chī là',
        "Please don't add too much chilli — I can't handle spice well.",
      ],
      ['服务员，买单！', 'fú wù yuán mǎi dān', 'Waiter — the bill, please!'],
    ],
  },
  {
    id: 'isl_taxi',
    title: 'Taking a taxi',
    titleZh: '打车',
    useWhen: 'You need this one while stressed and carrying luggage. Overlearn it.',
    tags: ['transactional', 'travel'],
    hskLevel: 3,
    blocks: [
      ['师傅，去首都机场，谢谢。', 'shī fu qù shǒu dū jī chǎng xiè xie', 'Driver, to Capital Airport, thanks.', '师傅 is the right address for a driver. 先生 sounds stiff and foreign.'],
      ['麻烦您走高速，我有点赶时间。', 'má fan nín zǒu gāo sù wǒ yǒu diǎn gǎn shí jiān', "Please take the expressway — I'm in a bit of a hurry."],
      ['大概要多长时间？', 'dà gài yào duō cháng shí jiān', 'Roughly how long will it take?'],
      ['就在前面路口停就行。', 'jiù zài qián miàn lù kǒu tíng jiù xíng', 'Just pull over at the intersection ahead.'],
      ['可以用微信支付吗？', 'kě yǐ yòng wēi xìn zhī fù ma', 'Can I pay by WeChat?'],
    ],
  },
]

/**
 * Factory content carries the lowest possible sync clock.
 *
 * Stamping seeds with the moment a device first opened the app would make a
 * brand-new device's untouched starter island *newer* than a real edit made on
 * another device last week — and last-write-wins would hand the win to the
 * factory copy, silently destroying the edit. At zero, any genuine change beats
 * a seed, and two devices' untouched seeds tie instead of fighting.
 *
 * Restoring starter content later is a deliberate act, so that path goes
 * through the reducer, which stamps a real clock.
 */
const SEED_CLOCK = 0

function materialise(seeds: SeedIsland[], now: number): Island[] {
  return seeds.map((seed) => ({
    id: seed.id,
    title: seed.title,
    titleZh: seed.titleZh,
    useWhen: seed.useWhen,
    tags: seed.tags,
    hskLevel: seed.hskLevel,
    blocks: seed.blocks.map(([hanzi, pinyin, english, note], i) => ({
      id: `${seed.id}_b${i + 1}`,
      hanzi,
      pinyin,
      english,
      ...(note ? { note } : {}),
    })),
    srs: freshSrs(),
    stats: { performances: 0, bestScore: 0, lastScore: null, lastDrilledAt: null },
    language: 'zh',
    isStarter: true,
    createdAt: now,
    updatedAt: SEED_CLOCK,
  }))
}

/**
 * Everything, both difficulties.
 *
 * BEGINNER_ZH covers the same five identity topics as the head of SEEDS, at
 * HSK 1-2 instead of 3, and both are here on purpose. Which set you see first
 * is decided by your level when the list is drawn, not by what got installed —
 * so raising your level is instant and reversible, and nothing has to be
 * deleted or re-seeded for it.
 *
 * They lead the array because the level split preserves the order it is given,
 * and the first thing a beginner should meet is who they are — work, why they
 * study, family, where they live, what they do — not 在餐厅点菜, which is what
 * they got when the HSK 3 identity islands dropped below the line and the next
 * HSK 2 island in the array floated up to the top. Existing stores are not
 * affected either way: the backfill appends, so nobody's list reorders.
 */
export function buildSeedIslands(now = Date.now()): Island[] {
  return materialise(
    [...BEGINNER_ZH, ...SEEDS, ...MORE_ZH, ...EXTRA_ZH, ...NUMBERS_ZH, ...ADVANCED_ZH],
    now,
  )
}

/**
 * The HSK 1-2 identity set alone, for stores that predate it.
 *
 * Seeding runs once, on a first install, so without this the people who most
 * need these islands — everyone already using the app — would be the only ones
 * who never got them.
 */
export function buildBeginnerIslands(now = Date.now()): Island[] {
  return materialise(BEGINNER_ZH, now)
}
